import { useState } from 'react'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { resendVerifyEmail } from '@/lib/auth/resend-api'
import type { OrgType } from './signup-types'

interface AccountCreatedScreenProps {
  email: string
  orgType: OrgType
}

type ResendState = 'idle' | 'sending' | 'sent' | 'rate_limited' | 'error'

const CLIENT_NEXT_COPY =
  "After you verify your email, we’ll take you straight to onboarding. Start by uploading the entity list your organization already uses. EntityOS will show you what needs to be reviewed before you go live."

const AGENCY_NEXT_COPY =
  "After you verify your email, we’ll take you straight to Agency onboarding. Start by creating your first client, then upload that client’s entity list. EntityOS will guide you through the review before you move on to more clients."

export function AccountCreatedScreen({ email, orgType }: AccountCreatedScreenProps) {
  const [resendState, setResendState] = useState<ResendState>('idle')
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  function showToast(message: string) {
    setToastMessage(message)
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 4000)
  }

  async function handleResend() {
    setResendState('sending')
    const outcome = await resendVerifyEmail(email)
    if (outcome.ok) {
      setResendState('sent')
      showToast('Verification email sent again.')
    } else if (outcome.reason === 'rate_limited') {
      setResendState('rate_limited')
      showToast('Too many requests — please wait a moment and try again.')
    } else {
      setResendState('error')
      showToast('Could not resend — check your connection and try again.')
    }
    // Re-enable after 5 seconds
    setTimeout(() => setResendState('idle'), 5000)
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Envelope icon */}
      <div
        className="flex h-[62px] w-[62px] items-center justify-center rounded-[19px] bg-primary/10 text-primary"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          width="29"
          height="29"
        >
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m4 7 8 6 8-6" />
        </svg>
      </div>

      <div>
        <Text variant="headline-sm" as="h2" className="mb-2">
          Thank you. Please verify your email.
        </Text>
        <Text variant="body-sm" color="muted">
          We&apos;ve sent a verification link to the work email you used during signup. Click the
          link in that email to complete your account setup.
        </Text>
      </div>

      {/* Email box */}
      <div
        data-testid="account-created-email-box"
        className="rounded-[10px] border border-primary/30 bg-primary/5 px-3 py-2.5"
      >
        <Text variant="label-sm" color="primary" className="mb-1 text-[8px] uppercase tracking-wider">
          Verification email sent to
        </Text>
        <Text variant="body-sm" color="primary" className="font-semibold">
          {email}
        </Text>
      </div>

      {/* What you need to do */}
      <div
        data-testid="account-created-what-to-do"
        className="rounded-[10px] border border-border bg-surface-muted px-3 py-2.5"
      >
        <Text variant="label-sm" className="mb-1">
          What you need to do
        </Text>
        <Text variant="body-sm" color="muted">
          Open the EntityOS email and click the verification link. Once verified, you can continue
          with onboarding.
        </Text>
        <Text variant="body-sm" color="muted" className="mt-2">
          {accountCreatedNextCopy(orgType)}
        </Text>
      </div>

      {/* Spam / junk guidance */}
      <div
        data-testid="account-created-spam-guidance"
        className="rounded-[10px] border border-border bg-surface px-3 py-2.5"
      >
        <Text variant="label-sm" className="mb-1">
          Didn&apos;t receive the email?
        </Text>
        <Text variant="body-sm" color="muted">
          Please check your <strong>Spam or Junk</strong> folder first. If you find the EntityOS
          email there, mark it as <strong>&ldquo;Not spam&rdquo;</strong> so future account and
          security emails reach your inbox.
        </Text>
      </div>

      {/* Resend button */}
      <Button
        data-testid="account-created-resend-btn"
        onClick={() => void handleResend()}
        disabled={resendState === 'sending'}
        className="w-full"
      >
        {resendState === 'sending' ? (
          <span className="flex items-center justify-center gap-2">
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden
            />
            Sending…
          </span>
        ) : (
          'Resend verification email'
        )}
      </Button>

      <Text variant="body-sm" color="muted" className="text-center text-[8px]">
        For security, verification links may expire after a limited period.
      </Text>

      {/* Toast */}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'fixed bottom-4 right-4 rounded-[9px] bg-text px-3 py-2 text-[9px] text-surface transition-all duration-200',
          toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1.5 pointer-events-none',
        )}
      >
        {toastMessage}
      </div>
    </div>
  )
}

/** Left-panel "what happens next" copy — fork-variant, used by SignupPage */
// eslint-disable-next-line react-refresh/only-export-components -- utility co-located intentionally; moving it breaks the SignupPage import chain and the function is tightly coupled to this component's copy strings
export function accountCreatedNextCopy(orgType: OrgType): string {
  return orgType === 'agency' ? AGENCY_NEXT_COPY : CLIENT_NEXT_COPY
}
